import { Link } from "react-router-dom";
import FeeReceipt from "../../components/FeeReceipt";
import ExportExcel from "../../components/ExportExcel";
import { server } from "../../server";
import axios from "axios";
import * as XLSX from "xlsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { getDecodedToken } from "../../auth";
import DcrSchoolPDF from "../../components/DcrSchoolPDF";
import DcrSchoolExcel from "../../components/DcrSchoolExcel";
import FinancialYearReportPDF from "../../components/FinancialYearReportPDF";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdmissionFormDetails } from "../../redux/admissionFormSlice";
import { fetchMiscelleneousFeesDetails } from "../../redux/miscelleneousFeesSlice";
import { fetchDuesFeeDetails } from "../../redux/duesFeeSlice";

// ✅ Builds the selectable Financial Year options ("2024-25", "2025-26", ...)
// centred on today's date: 2 years back through 1 year forward.
const getFinancialYearOptions = () => {
  const now = new Date();
  const currentFYStart =
    now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const options = [];
  for (let i = -1; i <= 0; i++) {
    const startYear = currentFYStart + i;
    const endYearShort = String((startYear + 1) % 100).padStart(2, "0");
    options.push({ label: `${startYear}-${endYearShort}`, startYear });
  }
  return options;
};

// ✅ Financial year "2025-26" → { fromDate: "2025-04-01", toDate: "2026-03-31" }
const getFYDateRange = (startYear) => ({
  fromDate: `${startYear}-04-01`,
  toDate: `${startYear + 1}-03-31`,
});

// ✅ Split a financial year into two 6-month halves so the from-to date API
// (which can time out / return an oversized payload for a full 12-month
// pull) is called twice instead of once: Apr–Sep, then Oct–Mar.
const getFYHalves = (startYear) => [
  {
    label: "Apr \u2013 Sep",
    fromDate: `${startYear}-04-01`,
    toDate: `${startYear}-09-30`,
  },
  {
    label: "Oct \u2013 Mar",
    fromDate: `${startYear}-10-01`,
    toDate: `${startYear + 1}-03-31`,
  },
];

function MonthlyFees() {
  const [monthlyFees, setMonthlyFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeReport, setActiveReport] = useState("dcr");
  const fromDateInputRef = useRef(null);
  const toDateInputRef = useRef(null);
  const recordsPerPage = 10;

  // ✅ Which payment mode(s) should the downloaded DCR report contain.
  // ["All"] = original "All in One" behaviour (every mode + admission form + misc fee tables).
  // Any other combination = only the selected mode(s) tables are generated.
  const [selectedModes, setSelectedModes] = useState(["All"]);

  // ✅ Report file format for DCR / Generate Report: "pdf" or "excel".
  const [reportFormat, setReportFormat] = useState("pdf");

  // ✅ When reportFormat === "excel", how the workbook should be split into
  // sheets: "single" (All in 1 Sheet), "monthly" (Monthly Separate Sheet),
  // or "date" (Date Separate Sheet).
  const [excelSheetMode, setExcelSheetMode] = useState("single");

  // ✅ Financial Report — financial year, report type ("summary" / "detailed"),
  // and an in-flight flag so the "Generate" button can show a loading state.
  const financialYearOptions = useMemo(() => getFinancialYearOptions(), []);
  const [selectedFY, setSelectedFY] = useState(null);
  const [financialReportType, setFinancialReportType] = useState("summary");
  const [generatingFinancialReport, setGeneratingFinancialReport] =
    useState(false);
  // ✅ Shown next to the Generate button while the two half-year API calls
  // are in flight, e.g. "Fetching Apr – Sep... (1/2)".
  const [financialReportProgress, setFinancialReportProgress] = useState("");

  const [user, setUser] = useState(null);

  const dispatch = useDispatch();
  const { data: admissionFormDetails } = useSelector(
    (state) => state.admissionForm,
  );
  const { data: miscelleneousFeesDetails } = useSelector(
    (state) => state.miscelleneousFees,
  );
  const { data: duesFeeDetails } = useSelector((state) => state.duesFee);

  useEffect(() => {
    dispatch(fetchAdmissionFormDetails());
    dispatch(fetchMiscelleneousFeesDetails());
    dispatch(fetchDuesFeeDetails());
  }, [dispatch]);

  // ✅ Default the Financial Year selector to whichever FY contains today's
  // date, falling back to the most recent option.
  useEffect(() => {
    if (selectedFY || !financialYearOptions.length) return;
    const now = new Date();
    const currentFY = financialYearOptions.find((fy) => {
      const { fromDate, toDate } = getFYDateRange(fy.startYear);
      return now >= new Date(fromDate) && now <= new Date(toDate);
    });
    setSelectedFY(
      currentFY || financialYearOptions[financialYearOptions.length - 2],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialYearOptions]);

  const parseDDMMYYYY = (str) => {
    if (!str) return null;
    const [day, month, year] = str.split("/").map(Number);
    if (!day || !month || !year) return null;
    return new Date(year, month - 1, day);
  };

  const paymentModes = [
    { name: "Cash", label: "Cash" },
    { name: "UPI", label: "UPI" },
    { name: "NEFT", label: "NEFT" },
    { name: "Card Swap", label: "Card Swap" },
    { name: "Net Banking", label: "Net Banking" },
    { name: "Online", label: "Online Payment" },
  ];

  // ✅ Payment modes offered in the "Report Mode" selector for DCR / Generate Report.
  const paymentModeOptions = paymentModes.map((m) => m.name);

  // ✅ Extra report sections — independent on/off toggles. If picked, that
  // section's full table is included in the DCR PDF; if not picked (and
  // "All in One" isn't selected either), the section is left out entirely.
  const extraReportSections = ["Admission Form Fee", "Misc Fee"];

  // ✅ Excel-only: how the generated workbook should be split into sheets.
  const excelSheetModeOptions = [
    { value: "single", label: "All in 1 Sheet" },
    { value: "monthly", label: "Monthly Separate Sheet" },
    { value: "date", label: "Date Separate Sheet" },
  ];

  const toggleModeSelection = (mode) => {
    setSelectedModes((prev) => {
      if (mode === "All") return ["All"];

      let updated = prev.includes("All") ? [] : [...prev];
      if (updated.includes(mode)) {
        updated = updated.filter((m) => m !== mode);
      } else {
        updated.push(mode);
      }
      return updated.length === 0 ? ["All"] : updated;
    });
  };

  useEffect(() => {
    const decodedData = getDecodedToken();
    if (decodedData) {
      setUser(decodedData);
    }
  }, []);

  const userRole = user?.roles || [];
  const userId = user?.userId;

  // Fetch student data based on admission number
  const fetchStudent = async (admissionNo) => {
    if (!admissionNo) {
      setError("Please enter Admission No");
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${server}/getParticularStudentMongodb`,
        { admission_no: admissionNo },
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );
      const studentData = response.data?.["body-json"]?.body?.student;
      if (studentData) {
        return studentData;
      } else {
        setError("No Student Data Found");
        return null;
      }
    } catch (err) {
      setError("Student not found or API error.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async (feeData) => {
    try {
      const student = await fetchStudent(feeData.admission_no);
      if (student) {
        FeeReceipt([{ ...feeData, student }], student);
      } else {
        console.error("Student data not found, cannot generate receipt.");
      }
    } catch (error) {
      console.error("Error fetching student details:", error);
    }
  };

  // Fetch monthly fee records
  const fetchmonthlyFees = async () => {
    setLoading(true);

    const todayDate = new Date().toISOString().split("T")[0];
    const isAdmin =
      userRole.includes("Master Admin") || userRole.includes("Admin");

    const endpoint = isAdmin
      ? `${server}/feeHistory_dateWise_mongodb`
      : `${server}/feeHistory_collectedBy_filter`;

    const payload = isAdmin ? { date: todayDate } : { collectedBy: userId };

    try {
      const response = await axios.post(endpoint, payload, {
        headers: { Authorization: `${localStorage.getItem("token")}` },
      });

      const responseData = response.data?.["body-json"]?.body?.data;

      if (Array.isArray(responseData)) {
        const filteredData = responseData.filter((entry) => {
          const entryDate = new Date().toISOString().split("T")[0];
          return entryDate === todayDate;
        });

        filteredData.sort((a, b) => new Date(b.date) - new Date(a.date));
        setMonthlyFees(filteredData);
      } else {
        console.error("Invalid data format from API", response.data);
      }
    } catch (error) {
      console.error("Error fetching fee details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userRole && Array.isArray(userRole)) {
      fetchmonthlyFees();
    }
  }, [userRole]);

  // Filter records based on search term
  const filteredRecords = monthlyFees.filter((feesMaster) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(feesMaster || {}).some(
      (value) =>
        typeof value === "string" && value.toLowerCase().includes(searchLower),
    );
  });

  // Pagination calculations
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Page navigation functions
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Column mapping for export
  const columnMapping = {
    class: "Class",
    admissionFee: "Admission Fee",
    monthlyFee: "Monthly Fee",
    developmentCharges: "Development Charges",
    labFee: "Lab Fee",
  };

  // Handle exporting to Excel and PDF
  const handleExportExcel = () => {
    ExportExcel(monthlyFees, "FeesMaster", columnMapping);
  };

  const modeTotals = {
    Cash: 0,
    UPI: 0,
    NEFT: 0,
    Cheque: 0,
    "Card Swap": 0,
    "Net Banking": 0,
    Online: 0,
  };

  monthlyFees.forEach((monthlyFee) => {
    const feeDetails = monthlyFee.feeDetails;
    const allFees = [
      feeDetails.developmentCharges,
      feeDetails.admissionFee,
      ...(feeDetails.monthPaid || []),
    ];

    allFees.forEach((fee) => {
      if (fee?.paid) {
        Object.entries(fee.paymentMode || {}).forEach(([mode, value]) => {
          const amount = parseFloat(value?.amount) || 0;
          if (modeTotals.hasOwnProperty(mode)) {
            modeTotals[mode] += amount;
          }
        });
      }
    });
  });

  const totalAmount = Object.values(modeTotals).reduce(
    (sum, value) => sum + value,
    0,
  );

  const handleDownloadDateWiseReport = async () => {
    try {
      const dateInput = dateInputRef.current?.value;
      if (!dateInput) {
        toast.error("Select Date", { position: "top-right" });
        return;
      }

      const response = await axios.post(
        `${server}/feeHistory_dateWise_mongodb`,
        { date: dateInput },
        {
          headers: { Authorization: `${localStorage.getItem("token")}` },
        },
      );
      const responseData = response.data?.["body-json"]?.body?.data;
      if (!Array.isArray(responseData)) {
        toast.error("No Records Found", { position: "top-right" });
        return;
      }

      // Same DD/MM/YYYY format used by AdmissionForm.js / MiscellaneousFees.js
      // for their own date-wise filtering — kept consistent here.
      const formattedDateForFilter = new Date(dateInput).toLocaleDateString(
        "en-GB",
      );

      const admissionFormFiltered = (admissionFormDetails || []).filter(
        (item) => item.applyDate === formattedDateForFilter,
      );

      const miscFeesFiltered = (miscelleneousFeesDetails || []).filter(
        (item) => {
          const recordDate = item.createdAt
            ? new Date(item.createdAt).toLocaleDateString("en-GB")
            : null;
          return recordDate === formattedDateForFilter;
        },
      );

      const formattedData = [{ date: dateInput, monthlyFees: responseData }];

      if (reportFormat === "excel") {
        DcrSchoolExcel(
          formattedData,
          admissionFormFiltered,
          miscFeesFiltered,
          selectedModes,
          excelSheetMode,
        );
      } else {
        DcrSchoolPDF(
          formattedData,
          "original",
          admissionFormFiltered,
          miscFeesFiltered,
          selectedModes,
        );
      }
      toast.success("Report Generated");
    } catch (error) {
      toast.error("Please Try Again", { position: "top-right" });
    }
  };

  const handleDownloadFromToDateWiseReport = async () => {
    try {
      const fromDate = fromDateInputRef.current?.value;
      const toDate = toDateInputRef.current?.value;

      if (!fromDate || !toDate) {
        toast.error(!fromDate ? "Select From Date" : "Select To Date", {
          position: "top-right",
        });
        return;
      }

      const response = await axios.post(
        `${server}/feeHistory_fromDate_toDate_mongodb`,
        {
          fromDate: fromDate,
          toDate: toDate,
        },
        {
          headers: { Authorization: `${localStorage.getItem("token")}` },
        },
      );
      const responseData = response.data?.["body-json"]?.body?.feeHistory;
      if (!Array.isArray(responseData)) {
        toast.error("No Records Found", { position: "top-right" });
        return;
      }

      const fromD = new Date(fromDate);
      fromD.setHours(0, 0, 0, 0); // anchor to the very start of the "from" day (local time)
      const toD = new Date(toDate);
      toD.setHours(23, 59, 59, 999); // include the entire "to" day

      const admissionFormFilteredRange = (admissionFormDetails || []).filter(
        (item) => {
          const d = parseDDMMYYYY(item.applyDate);
          return d && d >= fromD && d <= toD;
        },
      );

      const miscFeesFilteredRange = (miscelleneousFeesDetails || []).filter(
        (item) => {
          const d = item.createdAt ? new Date(item.createdAt) : null;
          return d && d >= fromD && d <= toD;
        },
      );

      const formattedData = [
        { date: fromDate, toDate: toDate, monthlyFees: responseData },
      ];

      if (reportFormat === "excel") {
        DcrSchoolExcel(
          formattedData,
          admissionFormFilteredRange,
          miscFeesFilteredRange,
          selectedModes,
          excelSheetMode,
        );
      } else {
        DcrSchoolPDF(
          formattedData,
          "original",
          admissionFormFilteredRange,
          miscFeesFilteredRange,
          selectedModes,
        );
      }
      toast.success("Report Generated");
    } catch (error) {
      toast.error("Please Try Again", { position: "top-right" });
    }
  };

  // ✅ Financial Report — pulls the selected FY in two 6-month API calls
  // (Apr–Sep, then Oct–Mar) instead of one 12-month call, merges the two
  // result sets, filters admission form & misc fee data to the full FY
  // range, and hands everything (plus the current dues snapshot) off to
  // FinancialYearReportPDF.
  const handleGenerateFinancialReport = async () => {
    if (!selectedFY) {
      toast.error("Select Financial Year", { position: "top-right" });
      return;
    }

    setGeneratingFinancialReport(true);
    setFinancialReportProgress("");
    try {
      const halves = getFYHalves(selectedFY.startYear);
      let combinedFeeHistory = [];

      for (let i = 0; i < halves.length; i++) {
        const half = halves[i];
        setFinancialReportProgress(
          `Fetching ${half.label}... (${i + 1}/${halves.length})`,
        );

        const response = await axios.post(
          `${server}/feeHistory_fromDate_toDate_mongodb`,
          { fromDate: half.fromDate, toDate: half.toDate },
          {
            headers: { Authorization: `${localStorage.getItem("token")}` },
          },
        );
        const halfData = response.data?.["body-json"]?.body?.feeHistory;
        if (Array.isArray(halfData)) {
          combinedFeeHistory = combinedFeeHistory.concat(halfData);
        }
      }

      if (combinedFeeHistory.length === 0) {
        toast.error("No Records Found For Selected Financial Year", {
          position: "top-right",
        });
        return;
      }

      setFinancialReportProgress("Building report...");

      const { fromDate, toDate } = getFYDateRange(selectedFY.startYear);
      const fromD = new Date(fromDate);
      fromD.setHours(0, 0, 0, 0);
      const toD = new Date(toDate);
      toD.setHours(23, 59, 59, 999);

      const admissionFormFilteredRange = (admissionFormDetails || []).filter(
        (item) => {
          const d = parseDDMMYYYY(item.applyDate);
          return d && d >= fromD && d <= toD;
        },
      );

      const miscFeesFilteredRange = (miscelleneousFeesDetails || []).filter(
        (item) => {
          const d = item.createdAt ? new Date(item.createdAt) : null;
          return d && d >= fromD && d <= toD;
        },
      );

      const formattedData = [
        { date: fromDate, toDate: toDate, monthlyFees: combinedFeeHistory },
      ];

      FinancialYearReportPDF({
        feeData: formattedData,
        admissionFormData: admissionFormFilteredRange,
        miscellaneousFeesData: miscFeesFilteredRange,
        duesFeeData: duesFeeDetails || [],
        financialYearLabel: selectedFY.label,
        reportType: financialReportType,
      });

      toast.success("Financial Report Generated");
    } catch (error) {
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setGeneratingFinancialReport(false);
      setFinancialReportProgress("");
    }
  };

  const handleExportTodaysFeeExcel = () => {
    const workbook = XLSX.utils.book_new();
    const today = new Date().toISOString().slice(0, 10); // Format: YYYY-MM-DD
    const todayReport = [];

    monthlyFees.forEach((fee) => {
      const devCharge = fee.feeDetails?.developmentCharges?.TotalAmt || 0;
      const admissionCharge = fee.feeDetails?.admissionFee?.TotalAmt || 0;
      const monthPaidToday = (fee.feeDetails?.monthPaid || []).filter(
        (m) => m.date === today,
      );

      // Skip if neither devCharge nor any month paid today
      if (
        devCharge === 0 &&
        admissionCharge === 0 &&
        monthPaidToday.length === 0
      ) {
        return;
      }

      let totalMonthlyFee = 0;
      let totalTransportFee = 0;
      let totalLabFee = 0;
      let totalAmtPaid = 0;
      const paymentModes = new Set();

      monthPaidToday.forEach((monthFee) => {
        totalMonthlyFee += monthFee.monthlyFee || 0;
        totalTransportFee += monthFee.transportFee || 0;
        totalLabFee += monthFee.labFee || 0;
        totalAmtPaid += monthFee.TotalAmt || 0;
        Object.keys(monthFee.paymentMode || {}).forEach((mode) =>
          paymentModes.add(mode),
        );
      });

      if (devCharge > 0 && fee.feeDetails?.developmentCharges?.date === today) {
        totalAmtPaid += devCharge;
        Object.keys(
          fee.feeDetails?.developmentCharges?.paymentMode || {},
        ).forEach((mode) => paymentModes.add(mode));
      }

      if (admissionCharge > 0 && fee.feeDetails?.admissionFee?.date === today) {
        totalAmtPaid += admissionCharge;
        Object.keys(fee.feeDetails?.admissionFee?.paymentMode || {}).forEach(
          (mode) => paymentModes.add(mode),
        );
      }

      const formattedDate = new Date(fee.date)
        .toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
        .replace(",", "");

      todayReport.push({
        "Receipt Date": formattedDate,
        "Receipt No": fee.receipt_no,
        Session: fee.session,
        "Admission No": fee.admission_no,
        "Student Name": fee.student_name,
        Class: fee.class,
        Section: fee.currentSection,
        "Roll No": fee.rollNo,
        "Development Fee": devCharge,
        "Admission Fee": admissionCharge,
        "Monthly Fee": totalMonthlyFee,
        "Transport Fee": totalTransportFee,
        "Lab Fee": totalLabFee,
        "Total Amount Paid": totalAmtPaid,
        "Mode of Payment": Array.from(paymentModes).join(", "),
      });
    });

    if (todayReport.length === 0) {
      alert("No records found for today.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(todayReport);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Today's Report");
    XLSX.writeFile(workbook, `TodaysFeeReport_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportTodaysAmountCollectionExcel = () => {
    const workbook = XLSX.utils.book_new();
    const today = new Date().toISOString().slice(0, 10);
    const todayReport = [];

    monthlyFees.forEach((fee) => {
      const devCharge = fee.feeDetails?.developmentCharges?.TotalAmt || 0;
      const admissionCharge = fee.feeDetails?.admissionFee?.TotalAmt || 0;
      const monthPaidToday = (fee.feeDetails?.monthPaid || []).filter(
        (m) => m.date === today,
      );

      if (
        devCharge === 0 &&
        admissionCharge === 0 &&
        monthPaidToday.length === 0
      ) {
        return;
      }

      let totalMonthlyFee = 0;
      let totalTransportFee = 0;
      let totalLabFee = 0;
      let totalAmtPaid = 0;
      const paymentModes = new Set();

      monthPaidToday.forEach((monthFee) => {
        totalMonthlyFee += monthFee.monthlyFee || 0;
        totalTransportFee += monthFee.transportFee || 0;
        totalLabFee += monthFee.labFee || 0;
        totalAmtPaid += monthFee.TotalAmt || 0;
        Object.keys(monthFee.paymentMode || {}).forEach((mode) =>
          paymentModes.add(mode),
        );
      });

      if (devCharge > 0 && fee.feeDetails?.developmentCharges?.date === today) {
        totalAmtPaid += devCharge;
        Object.keys(
          fee.feeDetails?.developmentCharges?.paymentMode || {},
        ).forEach((mode) => paymentModes.add(mode));
      }

      if (admissionCharge > 0 && fee.feeDetails?.admissionFee?.date === today) {
        totalAmtPaid += admissionCharge;
        Object.keys(fee.feeDetails?.admissionFee?.paymentMode || {}).forEach(
          (mode) => paymentModes.add(mode),
        );
      }

      todayReport.push({
        "Monthly Fee": totalMonthlyFee,
        "Transport Fee": totalTransportFee,
        "Lab Fee": totalLabFee,
        "Total Amount Paid": totalAmtPaid,
        "Mode of Payment": Array.from(paymentModes).join(", "),
      });
    });

    if (todayReport.length === 0) {
      alert("No records found for today.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(todayReport);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Today's Collection");
    XLSX.writeFile(workbook, `TodaysAmountCollection_${today}.xlsx`);
    toast.success("Report Generated");
  };

  // ✅ Shared format selector UI used by both the "DCR Report" and
  // "Generate Report" panels. Choosing "Excel" reveals the sheet-layout
  // options (All in 1 Sheet / Monthly Separate Sheet / Date Separate Sheet).
  const renderFormatSelector = () => (
    <div className="mb-2">
      <p className="fs-8 fw-medium text-secondary mb-1">Report Format</p>
      <div className="d-flex flex-wrap gap-1 mb-2">
        <span
          onClick={() => setReportFormat("pdf")}
          className={`badge rounded-3 px-3 py-2 cursor-pointer ${
            reportFormat === "pdf"
              ? "bg-dark text-white"
              : "bg-white text-dark border"
          }`}
        >
          PDF
        </span>
        <span
          onClick={() => setReportFormat("excel")}
          className={`badge rounded-3 px-3 py-2 cursor-pointer ${
            reportFormat === "excel"
              ? "bg-dark text-white"
              : "bg-white text-dark border"
          }`}
        >
          Excel
        </span>
      </div>

      {reportFormat === "excel" && (
        <>
          <p className="fs-8 fw-medium text-secondary mb-1">
            Excel Sheet Layout
          </p>
          <div className="d-flex flex-wrap gap-1 mb-2">
            {excelSheetModeOptions.map((opt) => (
              <span
                key={opt.value}
                onClick={() => setExcelSheetMode(opt.value)}
                className={`badge rounded-3 px-3 py-2 cursor-pointer ${
                  excelSheetMode === opt.value
                    ? "bg-dark text-white"
                    : "bg-white text-dark border"
                }`}
              >
                {opt.label}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );

  // ✅ Shared mode-selector UI used by both the "DCR Report" and
  // "Generate Report" panels. Tapping "All in One" resets to the full
  // combined report; tapping individual modes builds a multi-select and
  // clears "All in One".
  const renderModeSelector = () => (
    <div className="mb-2">
      <p className="fs-8 fw-medium text-secondary mb-1">
        Report Mode &mdash; select "All in One" or one/more
      </p>
      <div className="d-flex flex-wrap gap-1 mb-1">
        <span
          onClick={() => toggleModeSelection("All")}
          className={`badge rounded-3 px-3 py-2 cursor-pointer ${
            selectedModes.includes("All")
              ? "bg-dark text-white"
              : "bg-white text-dark border"
          }`}
        >
          All in One
        </span>
        {paymentModeOptions.map((mode) => (
          <span
            key={mode}
            onClick={() => toggleModeSelection(mode)}
            className={`badge rounded-3 px-3 py-2 cursor-pointer ${
              selectedModes.includes(mode)
                ? "bg-dark text-white"
                : "bg-white text-dark border"
            }`}
          >
            {mode}
          </span>
        ))}
      </div>
      <div className="d-flex flex-wrap gap-1 mb-3">
        {extraReportSections.map((section) => (
          <span
            key={section}
            onClick={() => toggleModeSelection(section)}
            className={`badge rounded-3 px-3 py-2 cursor-pointer ${
              selectedModes.includes(section)
                ? "bg-dark text-white"
                : "bg-white text-dark border"
            }`}
          >
            {section}
          </span>
        ))}
      </div>
    </div>
  );

  // ✅ Financial Report panel — Financial Year selector, Summary/Detailed
  // toggle, and the Generate button. Rendered directly below "Download DCR".
  const renderFinancialReportSection = () => (
    <div>
      <div className="d-flex align-items-center gap-2 mb-2">
        <i className="bi bi-graph-up-arrow text-dark fs-6"></i>
        <p className="fs-6 fw-semibold text-dark mb-0">Financial Report</p>
      </div>
      <p className="fs-7 lh-sm text-secondary mb-2">
        Full financial year collection report (1 Apr &ndash; 31 Mar), with
        outstanding dues included
      </p>

      <p className="fs-8 fw-medium text-secondary mb-1">Financial Year</p>
      <div className="d-flex flex-wrap gap-1 mb-2">
        {financialYearOptions.map((fy) => (
          <span
            key={fy.label}
            onClick={() => setSelectedFY(fy)}
            className={`badge rounded-3 px-3 py-2 cursor-pointer ${
              selectedFY?.label === fy.label
                ? "bg-dark text-white border"
                : "bg-white text-dark border"
            }`}
          >
            {fy.label}
          </span>
        ))}
      </div>

      <p className="fs-8 fw-medium text-secondary mb-1">Report Type</p>
      <div className="d-flex flex-wrap gap-1 mb-2">
        <span
          onClick={() => setFinancialReportType("summary")}
          className={`badge rounded-3 px-3 py-2 cursor-pointer ${
            financialReportType === "summary"
              ? "bg-dark text-white"
              : "bg-white text-dark border"
          }`}
        >
          Summary Report
        </span>
        <span
          onClick={() => setFinancialReportType("detailed")}
          className={`badge rounded-3 px-3 py-2 cursor-pointer ${
            financialReportType === "detailed"
              ? "bg-dark text-white"
              : "bg-white text-dark border"
          }`}
        >
          Detailed Report
        </span>
      </div>

      <div className="d-flex align-items-center gap-2 flex-wrap mt-3">
        <span
          onClick={
            !generatingFinancialReport
              ? handleGenerateFinancialReport
              : undefined
          }
          className={`btn btn-sm px-4 rounded-pill ${
            generatingFinancialReport ? "btn-secondary disabled" : "btn-primary"
          }`}
        >
          {generatingFinancialReport ? (
            <span className="spinner-border spinner-border-sm" role="status" />
          ) : (
            "Generate Financial Report"
          )}
        </span>
        {generatingFinancialReport && financialReportProgress && (
          <span className="fs-8 text-secondary">{financialReportProgress}</span>
        )}
      </div>
    </div>
  );

  return (
    <div id="db-wrapper">
      <main id="page-content">
        <div className="">
          <div class="container-fluid my-3 px-3">
            <div className="row g-2">
              <div className="col-lg-8">
                <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                  <table class="table mb-0 text-nowrap table-centered">
                    <thead class="table-light">
                      <tr>
                        <th className="w-10">No.</th>
                        <th className="lh-1 w-60">
                          Details
                          <i className="bi bi-info-circle ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Displaying Information as per Record
                          </span>
                        </th>
                        <th className="lh-1 w-30">
                          Action
                          <i className="bi bi-person-gear ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Role-Specific Action Panel
                          </span>
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {currentRecords.map((monthlyFee, index) => {
                        const feeDetails = monthlyFee.feeDetails;
                        const devCharge = feeDetails.developmentCharges?.paid
                          ? feeDetails.developmentCharges.TotalAmt
                          : 0;
                        const admissionCharge = feeDetails.admissionFee?.paid
                          ? feeDetails.admissionFee.TotalAmt
                          : 0;
                        const monthPayments = feeDetails.monthPaid || [];
                        const totalMonthlyFee = monthPayments.reduce(
                          (sum, fee) => sum + (fee.monthlyFee || 0),
                          0,
                        );
                        const totalTransportFee = monthPayments.reduce(
                          (sum, fee) => sum + (fee.transportFee || 0),
                          0,
                        );
                        const totalLabFee = monthPayments.reduce(
                          (sum, fee) => sum + (fee.labFee || 0),
                          0,
                        );

                        const totalAmtPaid =
                          devCharge +
                          admissionCharge +
                          totalMonthlyFee +
                          totalTransportFee +
                          totalLabFee;

                        const paymentModes = new Set();
                        [
                          feeDetails.developmentCharges,
                          feeDetails.admissionFee,
                          ...monthPayments,
                        ]
                          .filter((fee) => fee?.paid)
                          .forEach((fee) => {
                            Object.keys(fee.paymentMode || {}).forEach((mode) =>
                              paymentModes.add(mode),
                            );
                          });

                        return (
                          <tr key={index}>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span className="text-dark">Receipt Date</span>{" "}
                              &mdash;{" "}
                              <span className="text-uppercase">
                                {new Date(monthlyFee.date)
                                  .toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })
                                  .replace(",", "")}
                              </span>
                              <br />
                              <span className="text-dark">Receipt No</span>{" "}
                              &mdash; {monthlyFee.receipt_no} <br />
                              <span className="text-dark">
                                Session
                              </span> &mdash; {monthlyFee.session} <br />
                              <span className="text-dark">
                                Admission No
                              </span>{" "}
                              &mdash; {monthlyFee.admission_no} <br />
                              <span className="text-dark">
                                Student Name
                              </span>{" "}
                              &mdash; {monthlyFee.student_name} <br />
                              <span className="text-dark">
                                Class
                              </span> &mdash; {monthlyFee.class} <br />
                              <span className="text-dark">
                                Section
                              </span> &mdash; {monthlyFee.currentSection} <br />
                              <span className="text-dark">
                                Roll No
                              </span> &mdash; {monthlyFee.rollNo}
                              <hr className="w-25" />
                              {devCharge > 0 && (
                                <>
                                  <span className="text-dark">
                                    Development Fee
                                  </span>{" "}
                                  &mdash; {devCharge}
                                  <br />
                                </>
                              )}
                              {admissionCharge > 0 && (
                                <>
                                  <span className="text-dark">
                                    Admission Fee{" "}
                                  </span>{" "}
                                  &mdash; {admissionCharge}
                                  <br />
                                </>
                              )}
                              <span className="text-dark">
                                Monthly Fee
                              </span>{" "}
                              &mdash; {totalMonthlyFee} <br />
                              <span className="text-dark">
                                Transport Fee
                              </span>{" "}
                              &mdash; {totalTransportFee} <br />
                              {totalLabFee > 0 && (
                                <>
                                  <span className="text-dark">Lab Fee</span>{" "}
                                  &mdash; {totalLabFee} <br />
                                </>
                              )}
                              <span className="text-dark">
                                Total Amount Paid
                              </span>{" "}
                              &mdash; {totalAmtPaid} <br />
                              <span className="text-dark">
                                Mode of Payment
                              </span>{" "}
                              &mdash; {Array.from(paymentModes).join(", ")}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span
                                  onClick={() =>
                                    handleDownloadReceipt(monthlyFee)
                                  }
                                  class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Receipt
                                </span>
                                <Link
                                  to="/collect-fees"
                                  state={{
                                    admissionNo: monthlyFee.admission_no,
                                  }}
                                  class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Fee Ledger
                                </Link>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-lg-4">
                <div class="">
                  <div className="mb-3">
                    <div className="d-flex bg-white justify-content-between rounded-4">
                      <form
                        className="d-flex align-items-center"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <span className="position-absolute ps-3 search-icon">
                          <i className="bi bi-filter-circle-fill ms-1" />
                        </span>
                        <input
                          type="search"
                          className="form-control ps-7 rounded-3 border-0 rounded-4"
                          placeholder="Filter Data"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </form>
                      <Link
                        to="/collect-fees"
                        className="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                      >
                        Collect Fees
                      </Link>
                    </div>
                    <div className="mt-2 ms-4">
                      <nav aria-label="breadcrumb">
                        <ol class="breadcrumb pe-3">
                          <li class="breadcrumb-item">
                            <a href="#">Fees Collection</a>
                          </li>
                          <li
                            class="breadcrumb-item active"
                            aria-current="page"
                          >
                            Monthly Fees
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>

                  <div className="p-4 bg-white rounded-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        {loading ? (
                          <div
                            className="spinner-border text-dark data-loader-spinner"
                            role="status"
                          ></div>
                        ) : (
                          <img src="/assets/images/folder.svg" />
                        )}

                        <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                          Monthly Fees
                          <br />
                          <span className="text-secondary fw-medium fs-6 lh-sm">
                            0.2 MB Size
                          </span>
                        </p>
                      </div>
                      <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                        Export
                      </span>
                    </div>

                    <div class="px-4 py-2 mt-3 bg-light rounded-4">
                      <li className="list-group-item px-0">
                        <a
                          className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                          data-bs-toggle="collapse"
                          href="#specifications"
                          role="button"
                          aria-expanded="false"
                          aria-controls="specifications"
                        >
                          <p className="m-0 fw-medium lh-sm text-dark">
                            Download Report
                          </p>
                          <span className="chevron-arrow ms-4">
                            <i className="bi bi-chevron-down fs-4" />
                          </span>
                        </a>
                        <div
                          className="collapse"
                          id="specifications"
                          data-bs-parent="#ecommerceAccordion"
                        >
                          <div className="py-3">
                            <div class="rounded-4 d-flex flex-wrap">
                              <span
                                onClick={handleExportTodaysFeeExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Today's Report
                              </span>
                              <span
                                onClick={
                                  handleExportTodaysAmountCollectionExcel
                                }
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Today's Collection Amount
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    </div>

                    <div class="mt-1">
                      <div className="row g-1">
                        {paymentModes.map(
                          (mode) =>
                            modeTotals[mode.name] > 0 && (
                              <div className="col-6" key={mode.name}>
                                <div
                                  className={`p-3 rounded-4 ${
                                    mode.name === "Online"
                                      ? "bg-success-soft"
                                      : "bg-danger-soft"
                                  }`}
                                >
                                  <h4 className="m-0 text-center lh-1">
                                    {modeTotals[mode.name].toLocaleString(
                                      "en-IN",
                                    )}
                                  </h4>
                                  <p className="text-center fs-5 m-0 lh-1 mt-1">
                                    {mode.label}
                                  </p>
                                </div>
                              </div>
                            ),
                        )}

                        <div className="col-6">
                          <div className="p-3 rounded-4 bg-info-soft">
                            <h4 className="m-0 text-center lh-1">
                              {totalAmount.toLocaleString("en-IN")}
                            </h4>
                            <p className="text-center fs-5 m-0 lh-1 mt-1">
                              Total Payment
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {(userRole?.includes("Master Admin") ||
                      userRole?.includes("Admin")) && (
                      <>
                        <div className="bg-secondary-soft p-2 rounded-top-4 mt-3 px-4">
                          <div className="row g-0 bg-white p-1 rounded-pill">
                            <div className="col-6">
                              <span
                                className={`btn btn-sm px-4 rounded-pill fw-medium border-0 w-100 ${
                                  activeReport === "dcr"
                                    ? "btn-dark"
                                    : "btn-transparent"
                                }`}
                                onClick={() => setActiveReport("dcr")}
                              >
                                DCR Report
                              </span>
                            </div>
                            <div className="col-6">
                              <span
                                className={`btn btn-sm px-4 rounded-pill fw-medium border-0 w-100 ${
                                  activeReport === "generate"
                                    ? "btn-dark"
                                    : "btn-transparent"
                                }`}
                                onClick={() => setActiveReport("generate")}
                              >
                                Generate Report
                              </span>
                            </div>
                          </div>
                        </div>

                        {activeReport === "dcr" && (
                          <div className="bg-secondary-soft rounded-bottom-4 py-3 px-4 mt-1">
                            {renderFormatSelector()}
                            {renderModeSelector()}
                            <div>
                              <input
                                type="date"
                                className="form-control ps-4 pe-5 border-0 rounded-pill"
                                placeholder="..."
                                ref={dateInputRef}
                              />
                            </div>
                            <span
                              onClick={handleDownloadDateWiseReport}
                              className="btn btn-primary btn-sm px-4 rounded-pill mt-2 me-1"
                            >
                              Download DCR
                            </span>
                          </div>
                        )}

                        {activeReport === "generate" && (
                          <div className="bg-secondary-soft rounded-bottom-4 py-3 px-4 mt-1">
                            {renderFormatSelector()}
                            {renderModeSelector()}
                            <div>
                              <input
                                type="date"
                                className="form-control ps-4 pe-5 border-0 rounded-pill"
                                placeholder="..."
                                ref={fromDateInputRef}
                              />
                              <input
                                type="date"
                                className="form-control ps-4 pe-5 border-0 rounded-pill mt-1"
                                placeholder="..."
                                ref={toDateInputRef}
                              />
                            </div>
                            <span
                              onClick={handleDownloadFromToDateWiseReport}
                              className="btn btn-primary btn-sm px-4 rounded-pill mt-2 me-1"
                            >
                              Download Report
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {(userRole?.includes("Master Admin") ||
                      userRole?.includes("Admin")) && (
                      <div className="bg-danger-soft rounded-4 p-4 mt-1">
                        {renderFinancialReportSection()}
                      </div>
                    )}
                  </div>

                  <div className="my-4">
                    <nav>
                      <ul className="pagination justify-content-center mb-0">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={prevPage}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>

                        {(() => {
                          const pageNumbers = [];
                          pageNumbers.push(1);

                          if (currentPage > 3) {
                            pageNumbers.push("left-ellipsis");
                          }

                          if (currentPage !== 1 && currentPage !== totalPages) {
                            pageNumbers.push(currentPage);
                          }

                          if (currentPage < totalPages - 2) {
                            pageNumbers.push("right-ellipsis");
                          }

                          if (totalPages > 1) {
                            pageNumbers.push(totalPages);
                          }

                          return pageNumbers.map((page, index) => {
                            if (
                              page === "left-ellipsis" ||
                              page === "right-ellipsis"
                            ) {
                              return (
                                <li key={index} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }

                            return (
                              <li
                                key={index}
                                className={`page-item ${
                                  currentPage === page ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            );
                          });
                        })()}

                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={nextPage}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default MonthlyFees;
